import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navTextClass = isHome && !scrolled
    ? "text-white/90 hover:text-white"
    : "text-foreground-700 hover:text-primary-600";

  const logoPrimaryClass = isHome && !scrolled
    ? "text-white"
    : "text-primary-600";

  const logoTextClass = isHome && !scrolled
    ? "text-white"
    : "text-foreground-900";

  const navBgClass = isHome && !scrolled
    ? "bg-transparent"
    : "bg-background-50/95 backdrop-blur-md shadow-sm";

  const mobileBgClass = isHome && !scrolled
    ? "bg-foreground-900/90 backdrop-blur-md"
    : "bg-background-50/95 backdrop-blur-md";

  const mobileTextClass = isHome && !scrolled
    ? "text-white/90 hover:text-white"
    : "text-foreground-700 hover:text-primary-600";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-lg md:text-xl font-heading font-bold tracking-tight transition-colors duration-300 ${logoTextClass}`}>
              AllChinaBuy <span className={logoPrimaryClass}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${navTextClass}`}>
              Home
            </Link>
            <a href="#categories" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}>
              Categories
            </a>
            <Link to="/allchinabuy-spreadsheet-guide" className={`text-sm font-medium transition-colors whitespace-nowrap ${navTextClass}`}>
              Shopping Guide
            </Link>
            <a href="#safety" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}>
              Safety
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-300 ${isHome && !scrolled ? "text-white" : "text-foreground-900"}`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className={`md:hidden pb-5 flex flex-col gap-2 rounded-b-2xl ${mobileBgClass}`}>
            <Link to="/" onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg ${mobileTextClass}`}>
              <i className="ri-home-4-line mr-2"></i>Home
            </Link>
            <a href="#categories" onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-grid-line mr-2"></i>Categories
            </a>
            <Link to="/allchinabuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg ${mobileTextClass}`}>
              <i className="ri-book-open-line mr-2"></i>Shopping Guide
            </Link>
            <a href="#safety" onClick={() => setMobileOpen(false)} className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-shield-check-line mr-2"></i>Safety
            </a>
            <div className="border-t border-white/10 my-1 mx-4"></div>
            <p className="px-4 py-1 text-xs text-white/50 uppercase tracking-wider">Quick Category Jump</p>
            <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className={`px-4 py-2 text-sm transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-footprint-line mr-2"></i>Shoes
            </a>
            <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className={`px-4 py-2 text-sm transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-t-shirt-line mr-2"></i>Hoodies/Sweaters
            </a>
            <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className={`px-4 py-2 text-sm transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-t-shirt-line mr-2"></i>T-Shirts
            </a>
            <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className={`px-4 py-2 text-sm transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-handbag-line mr-2"></i>Bags
            </a>
            <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className={`px-4 py-2 text-sm transition-colors rounded-lg cursor-pointer ${mobileTextClass}`}>
              <i className="ri-vip-diamond-line mr-2"></i>Accessories
            </a>
            <div className="border-t border-white/10 my-1 mx-4"></div>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mx-4 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all text-center whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}